import React, { useEffect } from 'react'

import { LntDatePicker, LntSelect } from '@app/components/base'
import { TopFilters as TopFiltersWrapper } from '@app/components'

import { SIZE } from '@app/constants'
import { FILTERS_PARAMS_KEYS } from '@app/api/constants'
import { FILTERS_PLACEHOLDERS } from '@app/constants/filters'
import { LOCATION_KEY, MONITORINGS_LISTS_KEY, MONITORINGS_TYPES_KEY } from '@app/constants/queryKeys'

import { getFilterValue } from '@app/utils/filters'
import { weekSelectFormat } from '@app/utils/date/formats'
import { toDayjsByWeek } from '@app/utils/filters/adapters'
import { getFilterFromEntities, getFilterFromEntitiesToLabelsIds } from '@app/utils/entitiesAdapters'
import { useFiltersSearch } from '@app/hooks'

import { getTypes } from '@app/api/routes/monitorings'
import { getStores } from '@app/api/routes/locationStores'
import { getClusters } from '@app/api/routes/locationСlusters'
import { getClusterGroups } from '@app/api/routes/locationClusterGroups'
import { getSalesOrganization } from '@app/api/routes/locationSalesOrganization'
import { getMonitoringListsPromo } from '@app/api/routes/monitoringListsPromo'

import { commonSelectProps, defaultWeekPeriod } from './configs'

import styles from './styles.module.scss'

import type { TopFilterProps } from './types'

export const TopFilters: React.FC<TopFilterProps> = ({ onSearch, initialFilters }) => {
  const {
    filters,
    canResetFilters,
    handleClearFilters,
    handleSearch,
    handleSelectFilterChange,
    setFilters,
    handleWeekChange
  } = useFiltersSearch({ initialFilters, onSearch })

  const resetDefaultPeriod = (): void => {
    setFilters(prev => ({
      ...prev,
      ...defaultWeekPeriod
    }))
  }

  const setDefaultPeriod = (): void => {
    const hasQueryData = !!filters.week

    if (hasQueryData) return

    resetDefaultPeriod()

    onSearch({
      ...filters,
      ...defaultWeekPeriod
    })
  }

  const handleClear = (): void => {
    handleClearFilters()
    resetDefaultPeriod()
  }

  useEffect(() => {
    setDefaultPeriod()
  }, [])

  return (
    <TopFiltersWrapper
      canReset={ canResetFilters }
      onClear={ handleClear }
      onSearch={ handleSearch }
    >
      <LntSelect
        { ...commonSelectProps }
        value={ filters.monitoringTypeIds ?? [] }
        onChange={ value => handleSelectFilterChange(FILTERS_PARAMS_KEYS.monitoringTypeIds, value) }
        placeholder={ FILTERS_PLACEHOLDERS.monitoringsTypeFilter }
        querySelect={ {
          queryKey: [MONITORINGS_TYPES_KEY.typesItems],
          queryFn: getTypes,
          select: getFilterFromEntities
        } }
      />

      <LntSelect
        { ...commonSelectProps }
        value={ filters.storeIds ?? [] }
        onChange={ value => handleSelectFilterChange(FILTERS_PARAMS_KEYS.storeIds, value) }
        placeholder={ FILTERS_PLACEHOLDERS.storesFilter }
        querySelect={ {
          queryKey: [LOCATION_KEY.locationStoresItems],
          queryFn: getStores,
          select: getFilterFromEntities
        } }
      />

      <LntSelect
        { ...commonSelectProps }
        value={ filters.clusterIds ?? [] }
        onChange={ value => handleSelectFilterChange(FILTERS_PARAMS_KEYS.clusterIds, value) }
        placeholder={ FILTERS_PLACEHOLDERS.clustersFilter }
        querySelect={ {
          queryKey: [LOCATION_KEY.locationClustersItems],
          queryFn: getClusters,
          select: getFilterFromEntities
        } }
      />

      <LntSelect
        { ...commonSelectProps }
        value={ filters.clustersGroupIds ?? [] }
        onChange={ value => handleSelectFilterChange(FILTERS_PARAMS_KEYS.clustersGroupIds, value) }
        placeholder={ FILTERS_PLACEHOLDERS.clusterGroupsFilter }
        querySelect={ {
          queryKey: [LOCATION_KEY.locationClusterGroupsItems],
          queryFn: getClusterGroups,
          select: getFilterFromEntities
        } }
      />

      <LntSelect
        { ...commonSelectProps }
        value={ filters.promoIds ?? [] }
        onChange={ value => handleSelectFilterChange(FILTERS_PARAMS_KEYS.promoIds, value) }
        placeholder={ FILTERS_PLACEHOLDERS.monitoringListsPromoFilter }
        querySelect={ {
          queryKey: [MONITORINGS_LISTS_KEY.promotion],
          queryFn: getMonitoringListsPromo,
          select: getFilterFromEntitiesToLabelsIds
        } }
      />

      <LntSelect
        { ...commonSelectProps }
        value={ filters.salesOrganizationIds ?? [] }
        onChange={ value => handleSelectFilterChange(FILTERS_PARAMS_KEYS.salesOrganizationIds, value) }
        placeholder={ FILTERS_PLACEHOLDERS.salesOrganizationFilter }
        querySelect={ {
          queryKey: [LOCATION_KEY.locationSalesOrganizationsItems],
          queryFn: getSalesOrganization,
          select: getFilterFromEntities
        } }
      />

      <LntDatePicker
        picker='week'
        allowClear={ false }
        size={ SIZE.middle }
        placeholder={ FILTERS_PLACEHOLDERS.chosenWeek }
        className={ styles.commonMonitoringScheduleDrawer__itemSize }
        value={ getFilterValue(filters, FILTERS_PARAMS_KEYS.week, toDayjsByWeek) }
        format={ weekSelectFormat }
        onChange={ value =>
          handleWeekChange(value, FILTERS_PARAMS_KEYS.archivedAtAfter, FILTERS_PARAMS_KEYS.archivedAtBefore)
        }
      />
    </TopFiltersWrapper>
  )
}
